import { FunctionFactory } from 'survey-core';
import { Goals } from './goals';
import { ActivityLevel } from './activity';
import { BodyFat } from './bodyfat';
import { calculateCalorieTarget } from '../features/calculations';
import { Experience } from './experience';
import { Gender } from './gender';
import { WeightUnit, getWeightPounds } from './weight';
import { Physiology } from './physiology';
import { NumberWorkouts } from './numberWorkouts';
import { loc, tSurvey } from '../i18n/survey';

enum CalorieTracking {
  NoIdea = 'NoIdea',
  EatingMore = 'EatingMore',
  EatingLess = 'EatingLess',
  PrettyClose = 'PrettyClose',
}

const caloriesQuestion = {
  name: 'calories',
  title: loc("quiz.calories.title"),
  description: `{goalDescription}`,
  type: 'radiogroup',
  choices: [
    {
      value: CalorieTracking.NoIdea,
      text: loc("quiz.intake.noIdea"),
    },
    {
      value: CalorieTracking.EatingMore,
      text: loc("quiz.intake.eatingMore"),
    },
    {
      value: CalorieTracking.EatingLess,
      text: loc("quiz.intake.eatingLess"),
    },
    {
      value: CalorieTracking.PrettyClose,
      text: loc("quiz.intake.prettyClose"),
    },
  ],
  isRequired: true,
};

const goalCaloriesTextExpression = {
  name: 'goalCaloriesText',
  expression: "renderGoalCaloriesText({goals})",
};

const caloriesTextExpression = {
  name: 'caloriesText',
  expression: "renderCaloriesText({weightUnit}, {weight}, {bodyFat}, {activity}, {experience}, {goals}, {gender}, {numberWorkouts}, {physiology})",
};

function renderGoalCaloriesText(params: any[]): string {
  const goals = params[0] as Goals;
  switch (goals) {
    case Goals.LoseFat:
      return `you need to eat less`;
    case Goals.BuildMuscle:
      return `you need to eat slightly more`;
    case Goals.BuildMuscleAndLoseFat:
      return `you need to eat slightly less`;
  }
}

function renderGoalDescription(params: any[]): string {
  const goals = params[0] as Goals;
  switch (goals) {
    case Goals.LoseFat:
      return tSurvey("quiz.calories.goalDescription.loseWeight");
    case Goals.BuildMuscle:
      return tSurvey("quiz.calories.goalDescription.buildMuscle");
    case Goals.BuildMuscleAndLoseFat:
      return tSurvey("quiz.calories.goalDescription.recomp");
  }
}

FunctionFactory.Instance.register('renderGoalDescription', renderGoalDescription);

const goalDescriptionExpression = {
  name: 'goalDescription',
  expression: "renderGoalDescription({goals})",
};

// round to the nearest 50
function roundToNearest50(num: number): number {
  return Math.round(num / 50) * 50;
}

function renderCaloriesText(params: any[]): string {
  if (params === undefined || params === null || !params[0] || !params[1] || !params[2] || !params[3] || !params[4] || !params[5] || !params[6] || !params[7]) return '';
  const weightUnit = params[0] as WeightUnit;
  const weightRaw = params[1] as number;
  const bodyFat = params[2] as BodyFat;
  const activityLevel = params[3] as ActivityLevel;
  const experience = params[4] as Experience;
  const goal = params[5] as Goals;
  const gender = params[6] as Gender;
  const nunberWorkouts = params[7] as NumberWorkouts;
  const physiology = params[8] as Physiology;

  const weightPounds = getWeightPounds(weightRaw, weightUnit);
  const target = calculateCalorieTarget(experience, goal, bodyFat, gender, weightPounds, activityLevel, nunberWorkouts, physiology);
  const roundedTarget = roundToNearest50(target);
  let targets = [roundedTarget - 100, roundedTarget + 100];
  // Ensure the minimum limit after considering the window
  targets[0] = Math.max(gender === Gender.Male || physiology === Physiology.Male ? 1500 : 1200, targets[0]);
  targets[1] = Math.max(targets[0] + 200, targets[1]);

  return `${targets[0]} - ${targets[1]}`
}

FunctionFactory.Instance.register('renderGoalCaloriesText', renderGoalCaloriesText);
FunctionFactory.Instance.register('renderCaloriesText', renderCaloriesText);

export {
  CalorieTracking,
  caloriesQuestion,
  goalCaloriesTextExpression,
  caloriesTextExpression,
  renderCaloriesText,
  goalDescriptionExpression,
}